/* eslint-disable max-lines */
import { ArrowPathIcon, BriefcaseIcon } from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import { FC, ReactNode, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import {
  ILuggageDetails,
  LuggagePartnerStatus
} from "../../../interfaces/reservations/Reservations.types";
import {
  adminCancelLuggageStorage,
  adminEditLuggageStorage,
  getLuggageDetails
} from "../../../services/Reservations.service";
import Button from "../../ui/Button";
import Modal from "../../ui/Modal";
import Status from "../../ui/Status";

interface ILuggageDetailsModalProps {
  show: boolean;
  onClose: () => void;
  uniqueJourneyId: string;
  onSuccess?: () => void;
}

const MAX_BAGS = 10;
const MAX_DAYS = 30;

const inputClass =
  "w-full px-3 py-2 border border-gray-300 rounded-lg " +
  "focus:outline-none focus:ring-2 focus:ring-primary/40";

const errMessage = (error: unknown, fallback: string) =>
  (error as { response?: { data?: { message?: string } } })?.response?.data
    ?.message || fallback;

const statusBadge = (status: LuggagePartnerStatus) => {
  switch (status) {
    case "confirmed":
      return <Status type="green" name="I konfirmuar" />;
    case "cancelled":
      return <Status type="red" name="I anuluar" />;
    case "test":
      return <Status type="gray" name="Test" />;
    default:
      return <Status type="orange" name="Në pritje" />;
  }
};

const Row: FC<{ label: string; value: ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between items-center py-1.5 border-b border-gray-100 last:border-0">
    <span className="text-sm text-gray-500">{label}</span>
    <span className="text-sm font-medium text-right">{value}</span>
  </div>
);

const LuggageDetailsModal: FC<ILuggageDetailsModalProps> = ({
  show,
  onClose,
  uniqueJourneyId,
  onSuccess
}) => {
  const [details, setDetails] = useState<ILuggageDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<"view" | "edit">("view");
  // Held as strings so the fields can be cleared while typing (a numeric state with a
  // `|| 0` fallback would trap a leading 0 and block re-entry). Parsed to numbers below.
  const [bagCount, setBagCount] = useState("1");
  const [dropOffDate, setDropOffDate] = useState("");
  const [dayCount, setDayCount] = useState("1");
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const loadDetails = async () => {
    setLoading(true);
    try {
      const res = await getLuggageDetails(uniqueJourneyId);
      setDetails(res.data);
    } catch (error) {
      toast.error(
        errMessage(error, "Ndodhi një gabim gjatë marrjes së të dhënave të valixhes")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      setMode("view");
      void loadDetails();
    }
  }, [show, uniqueJourneyId]);

  const startEdit = () => {
    if (!details) return;
    setBagCount(String(details.bagCount));
    setDropOffDate(dayjs(details.dropOffDate).format("YYYY-MM-DD"));
    setDayCount(String(details.dayCount));
    setMode("edit");
  };

  const bagCountNum = parseInt(bagCount, 10);
  const dayCountNum = parseInt(dayCount, 10);

  const pickUpPreview =
    dropOffDate && dayCountNum >= 1
      ? dayjs(dropOffDate)
          .add(dayCountNum - 1, "day")
          .format("DD MMM YYYY")
      : "—";

  const editInvalid =
    Number.isNaN(bagCountNum) ||
    bagCountNum < 1 ||
    bagCountNum > MAX_BAGS ||
    Number.isNaN(dayCountNum) ||
    dayCountNum < 1 ||
    dayCountNum > MAX_DAYS ||
    !dropOffDate;

  const editsExhausted = !!details && details.editCount >= details.maxEditCount;

  // A cancelled add-on (cancelled on its own, or via a cancelled journey) is read-only:
  // the modal keeps showing the details but disables the actions and retitles itself.
  const isCancelled = !!details?.isCancelled;

  const handleSave = async () => {
    if (editInvalid) return;
    setSaving(true);
    try {
      await adminEditLuggageStorage(uniqueJourneyId, bagCountNum, dropOffDate, dayCountNum);
      toast.success("Valixhja u përditësua me sukses");
      await loadDetails();
      setMode("view");
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(errMessage(error, "Ndodhi një gabim gjatë përditësimit të valixhes"));
    } finally {
      setSaving(false);
    }
  };

  const handleCancelBooking = async () => {
    setCancelling(true);
    try {
      await adminCancelLuggageStorage(uniqueJourneyId);
      toast.success("Valixhja u anulua me sukses");
      // Keep the modal open and reload: the add-on now comes back flagged cancelled, so the
      // view flips to the read-only "cancelled" state in place. The Valixhe tag persists —
      // onSuccess refreshes the list/detail so the tag stays (backend still reports it).
      await loadDetails();
      setMode("view");
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(errMessage(error, "Ndodhi një gabim gjatë anulimit të valixhes"));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Modal
      show={show}
      onClose={onClose}
      position="center"
      title={
        <span className="flex items-center gap-2 font-medium">
          <BriefcaseIcon className="w-5 h-5 text-primary" />
          {isCancelled ? "Valixhja është anuluar" : "Valixhja"}
        </span>
      }
      modalWidth="440px"
      calcHeight={false}
    >
      <div className="p-4">
        {loading ? (
          <div className="flex justify-center items-center py-10">
            <ArrowPathIcon className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : !details ? (
          <p className="text-center text-gray-500 py-8 text-sm">
            Të dhënat e valixhes nuk u gjetën.
          </p>
        ) : mode === "view" ? (
          <>
            <Row label="Numri i çantave" value={details.bagCount} />
            <Row
              label="Data e dorëzimit"
              value={dayjs(details.dropOffDate).format("DD MMM YYYY")}
            />
            <Row
              label="Data e tërheqjes"
              value={dayjs(details.pickUpDate).format("DD MMM YYYY")}
            />
            <Row label="Ditë ruajtjeje" value={details.dayCount} />
            <Row
              label="Çmimi"
              value={`${details.cost}${
                details.currency === "EUR" ? "€" : ` ${details.currency}`
              }`}
            />
            <Row label="Kodi TIR" value={details.partnerTirCode || "—"} />
            <Row
              label="Statusi te partneri"
              value={statusBadge(isCancelled ? "cancelled" : details.partnerBookingStatus)}
            />
            <Row
              label="Modifikime të mbetura"
              value={Math.max(details.maxEditCount - details.editCount, 0)}
            />

            <div className="flex gap-2 justify-end mt-5">
              <Button
                type="button"
                btnType="custom"
                onClick={handleCancelBooking}
                disabled={cancelling || isCancelled}
                className="bg-red-50 text-red-600 hover:bg-red-100 flex items-center"
              >
                {cancelling && (
                  <ArrowPathIcon className="w-4 h-4 animate-spin mr-2" />
                )}
                Anulo valixhen
              </Button>
              <Button
                type="button"
                onClick={startEdit}
                disabled={editsExhausted || isCancelled}
                title={
                  editsExhausted
                    ? `Valixhja mund të modifikohet maksimum ${details.maxEditCount} herë`
                    : undefined
                }
              >
                Modifiko
              </Button>
            </div>
            {editsExhausted && !isCancelled && (
              <p className="text-xs text-gray-500 mt-2 text-right">
                Valixhja mund të modifikohet maksimum {details.maxEditCount} herë.
              </p>
            )}
          </>
        ) : (
          <>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Numri i çantave (1–{MAX_BAGS})
                </label>
                <input
                  type="number"
                  min={1}
                  max={MAX_BAGS}
                  value={bagCount}
                  onChange={(e) => setBagCount(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data e dorëzimit
                </label>
                <input
                  type="date"
                  value={dropOffDate}
                  onChange={(e) => setDropOffDate(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ditë ruajtjeje (1–{MAX_DAYS})
                </label>
                <input
                  type="number"
                  min={1}
                  max={MAX_DAYS}
                  value={dayCount}
                  onChange={(e) => setDayCount(e.target.value)}
                  className={inputClass}
                />
              </div>
              <p className="text-xs text-gray-500">
                Data e tërheqjes: <span className="font-medium">{pickUpPreview}</span>
              </p>
            </div>

            <div className="flex gap-2 justify-end mt-5">
              <Button
                type="button"
                btnType="custom"
                onClick={() => setMode("view")}
                disabled={saving}
                className="bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                Kthehu
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={saving || editInvalid}
                className="flex items-center"
              >
                {saving && <ArrowPathIcon className="w-4 h-4 animate-spin mr-2" />}
                Ruaj
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default LuggageDetailsModal;
